import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line,
} from "recharts";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { PageHeader, Loading, DataTable, SeverityBadge, StatusBadge, Card, StatCard } from "../components/common";
import { Cpu, Bot, Network, FileText, ListChecks, Database, Shield, AlertTriangle, ClipboardCheck, History, Activity, Gauge, TrendingUp, Briefcase, Plug } from "lucide-react";

const uc = (r, nav) => <button onClick={() => r.usecase_id && nav(`/usecases/${r.usecase_id}`)} className="text-xs text-[var(--qatar)] underline">{r.usecase_name || r.usecase_id || "—"}</button>;

export function ModulePage({ moduleKey }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const cfg = CONFIG(nav)[moduleKey];

  useEffect(() => { setRows(null); api.get(cfg.endpoint).then((r) => setRows(r.data)); }, [moduleKey]);
  if (!rows) return <Loading />;

  return (
    <div>
      <PageHeader title={t(`nav.${cfg.navKey}`)} subtitle={cfg.subtitle?.replace("{n}", rows.length)} summary={`${t(`nav.${cfg.navKey}`)}. ${rows.length} synthetic records in the sandbox.`} />
      {cfg.stats && <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">{cfg.stats(rows).map((s, i) => <StatCard key={i} testid={`mod-stat-${i}`} label={s.label} value={s.value} icon={cfg.icon} accent={s.accent} />)}</div>}
      <DataTable testid={`${moduleKey}-table`} columns={cfg.columns} rows={rows} onRowClick={cfg.rowClick} pageSize={20} />
    </div>
  );
}

const CONFIG = (nav) => ({
  models: {
    navKey: "models", endpoint: "/models", icon: Cpu, subtitle: "{n} synthetic AI models in the registry",
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "name", label: "Model" }, { key: "provider", label: "Provider" }, { key: "type", label: "Type" },
      { key: "risk", label: "Risk", render: (r) => <SeverityBadge level={r.risk} /> },
      { key: "eval_score", label: "Eval", render: (r) => `${r.eval_score}%` },
      { key: "drift_status", label: "Drift", render: (r) => <StatusBadge status={r.drift_status} /> },
      { key: "lifecycle_status", label: "Status", render: (r) => <StatusBadge status={r.lifecycle_status} /> },
    ],
  },
  agents: {
    navKey: "agents", endpoint: "/agents", icon: Bot, subtitle: "{n} governed AI agents",
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "name", label: "Agent" }, { key: "purpose", label: "Purpose" }, { key: "model", label: "Model" },
      { key: "risk", label: "Risk", render: (r) => <SeverityBadge level={r.risk} /> },
      { key: "transaction_limits", label: "Txn Limit" },
      { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ],
  },
  rag: {
    navKey: "rag", endpoint: "/rag", icon: Network, subtitle: "{n} governed RAG applications",
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "application", label: "Application" }, { key: "vector_db", label: "Vector DB" },
      { key: "classification", label: "Classification" }, { key: "poisoning_test", label: "Poisoning Test", render: (r) => <StatusBadge status={r.poisoning_test} /> },
      { key: "security_status", label: "Security", render: (r) => <StatusBadge status={r.security_status} /> },
    ],
  },
  policies: {
    navKey: "policies", endpoint: "/policies", icon: FileText, subtitle: "{n} seeded AI policies (EN + AR)",
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "name_en", label: "Policy", render: (r) => <div><p className="font-medium">{r.name_en}</p><p dir="rtl" className="text-xs text-slate-400">{r.name_ar}</p></div> },
      { key: "version", label: "Version" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      { key: "owner", label: "Owner" }, { key: "review_date", label: "Review" },
    ],
  },
  controls: {
    navKey: "controls", endpoint: "/controls", icon: ListChecks, subtitle: "{n} reusable GRC controls",
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "name", label: "Control" }, { key: "domain", label: "Domain" }, { key: "type", label: "Type" },
      { key: "framework_mapping", label: "Framework" }, { key: "regulatory_mapping", label: "Regulation" },
      { key: "effectiveness", label: "Effectiveness", render: (r) => <StatusBadge status={r.effectiveness} /> },
    ],
  },
  data: {
    navKey: "data", endpoint: "/datagov", icon: Database, subtitle: "{n} data governance records",
    stats: (rows) => [
      { label: "Records", value: rows.length }, { label: "With PII", value: rows.filter((r) => r.pii).length, accent: "bg-amber-50 text-amber-600" },
      { label: "Avg DG Score", value: `${Math.round(rows.reduce((a, r) => a + r.score, 0) / (rows.length || 1))}%` },
      { label: "Qatar Residency", value: rows.length },
    ],
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "usecase_name", label: "Use Case", render: (r) => uc(r, nav) }, { key: "classification", label: "Classification" },
      { key: "pii", label: "PII", render: (r) => <StatusBadge status={r.pii ? "Open" : "Closed"} /> }, { key: "score", label: "DG Score", render: (r) => `${r.score}%` },
      { key: "pdpl_status", label: "PDPL", render: (r) => <StatusBadge status={r.pdpl_status} /> },
    ],
  },
  security: {
    navKey: "security", endpoint: "/security", icon: Shield, subtitle: "{n} AI security control tests",
    stats: (rows) => [
      { label: "Tests", value: rows.length }, { label: "Passed", value: rows.filter((r) => r.status === "Passed").length, accent: "bg-emerald-50 text-emerald-600" },
      { label: "Failed", value: rows.filter((r) => r.status === "Failed").length, accent: "bg-red-50 text-red-600" },
      { label: "In Review", value: rows.filter((r) => r.status === "In Review").length, accent: "bg-blue-50 text-blue-600" },
    ],
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "domain", label: "Domain" }, { key: "control_name", label: "Control" },
      { key: "usecase_name", label: "Use Case", render: (r) => uc(r, nav) },
      { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> }, { key: "owner", label: "Owner" },
    ],
  },
  risk: {
    navKey: "risk", endpoint: "/risks", icon: AlertTriangle, subtitle: "{n} risk assessments",
    stats: (rows) => [
      { label: "Total Risks", value: rows.length },
      { label: "Critical Open", value: rows.filter((r) => r.risk_level === "Critical" && r.status === "Open").length, accent: "bg-red-50 text-red-600" },
      { label: "High Open", value: rows.filter((r) => r.risk_level === "High" && r.status === "Open").length, accent: "bg-orange-50 text-orange-600" },
      { label: "Closed", value: rows.filter((r) => r.status === "Closed").length, accent: "bg-emerald-50 text-emerald-600" },
    ],
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "usecase_name", label: "Use Case", render: (r) => uc(r, nav) }, { key: "category", label: "Category" },
      { key: "inherent_risk", label: "Inherent" }, { key: "residual_risk", label: "Residual" },
      { key: "risk_level", label: "Level", render: (r) => <SeverityBadge level={r.risk_level} /> },
      { key: "treatment", label: "Treatment" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ],
  },
  compliance: {
    navKey: "compliance", endpoint: "/compliance", icon: ClipboardCheck, subtitle: "{n} compliance mappings",
    stats: (rows) => [
      { label: "Mappings", value: rows.length }, { label: "Compliant", value: rows.filter((r) => r.status === "COMPLIANT").length, accent: "bg-emerald-50 text-emerald-600" },
      { label: "Non-Compliant", value: rows.filter((r) => r.status === "NON-COMPLIANT").length, accent: "bg-red-50 text-red-600" },
      { label: "Not Assessed", value: rows.filter((r) => r.status === "NOT ASSESSED").length, accent: "bg-slate-100 text-slate-600" },
    ],
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "regulation", label: "Regulation", render: (r) => <span className="rounded bg-slate-900 px-1.5 py-0.5 font-mono text-[10px] text-white">{r.regulation}</span> },
      { key: "requirement", label: "Requirement" }, { key: "usecase_name", label: "Use Case", render: (r) => uc(r, nav) },
      { key: "framework", label: "Framework" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ],
  },
  audit: {
    navKey: "audit", endpoint: "/audit", icon: History, subtitle: "{n} immutable-style audit events",
    columns: [
      { key: "timestamp", label: "Time", render: (r) => <span className="font-mono text-xs">{new Date(r.timestamp).toLocaleString()}</span> },
      { key: "user", label: "User" }, { key: "role", label: "Role" }, { key: "action", label: "Action", render: (r) => <StatusBadge status={r.action} /> },
      { key: "object", label: "Object" }, { key: "object_id", label: "Object ID", render: (r) => <span className="font-mono text-xs">{r.object_id}</span> },
      { key: "reason", label: "Reason" },
    ],
  },
  monitoring: {
    navKey: "monitoring", endpoint: "/monitoring", icon: Activity, subtitle: "{n} continuous monitoring events",
    stats: (rows) => [
      { label: "Events", value: rows.length }, { label: "Critical", value: rows.filter((r) => r.severity === "Critical").length, accent: "bg-red-50 text-red-600" },
      { label: "Open", value: rows.filter((r) => r.status === "Open").length, accent: "bg-orange-50 text-orange-600" },
      { label: "Drift Events", value: rows.filter((r) => (r.type || "").includes("Drift")).length, accent: "bg-blue-50 text-blue-600" },
    ],
    columns: [
      { key: "timestamp", label: "Time", render: (r) => <span className="font-mono text-xs">{new Date(r.timestamp).toLocaleDateString()}</span> },
      { key: "type", label: "Event" }, { key: "usecase_name", label: "Use Case", render: (r) => uc(r, nav) },
      { key: "severity", label: "Severity", render: (r) => <SeverityBadge level={r.severity} /> },
      { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ],
  },
  portfolio: {
    navKey: "portfolio", endpoint: "/portfolios", icon: Briefcase, subtitle: "{n} AI portfolios",
    columns: [
      { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-bold text-[var(--qatar)]">{r.id}</span> },
      { key: "name", label: "Portfolio" }, { key: "industry", label: "Industry" },
      { key: "budget", label: "Budget", render: (r) => `$${(r.budget / 1e6).toFixed(1)}M` },
      { key: "expected_value", label: "Expected", render: (r) => `$${(r.expected_value / 1e6).toFixed(1)}M` },
      { key: "realised_value", label: "Realised", render: (r) => `$${(r.realised_value / 1e6).toFixed(1)}M` },
      { key: "usecases", label: "Use Cases" }, { key: "risk", label: "Risk", render: (r) => <SeverityBadge level={r.risk} /> },
    ],
  },
  integrations: {
    navKey: "integrations", endpoint: "/integrations", icon: Plug, subtitle: "{n} sandbox integrations",
    columns: [
      { key: "name", label: "Name" }, { key: "type", label: "Type" }, { key: "description", label: "Description" },
      { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ],
  },
});
